import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ClipboardCheck,
  Save,
  CheckCircle2,
  AlertCircle,
  Clock,
  Calendar,
  Layers,
  BookOpen,
  Check,
  X,
  AlertTriangle,
  Building2,
  Users,
  CheckSquare,
  XSquare,
  Slash,
  RefreshCw,
  Sparkles,
  Eye
} from 'lucide-react';
import { MarksEntryStatusModule } from '../components/MarksEntryStatusModule';
import { StudentAcademicHistoryModal } from '../components/StudentAcademicHistoryModal';

interface StudentRosterItem {
  student_id: number;
  student_name: string;
  srn: string;
  roll_number: string;
  gender: string;
  section: string;
  stream: string | null;
  enrollment_status: string;
  status: 'PRESENT' | 'ABSENT' | 'NOT_APPLICABLE';
  obtained_marks: number | string;
  is_absent: boolean;
  remarks: string;
}

export const MonthlyAssessmentPage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();

  // 4-Step Selection States
  const [academicSession, setAcademicSession] = useState<string>('2026-27');
  const [months, setMonths] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('August');
  const [selectedClass, setSelectedClass] = useState<number>(1); // 1 = Class 10, 2 = Class 12
  const [selectedStream, setSelectedStream] = useState<string>('Science'); // Arts, Science, Commerce
  const [selectedSubject, setSelectedSubject] = useState<number>(2); // Mathematics
  const [maximumMarks, setMaximumMarks] = useState<number>(20);

  // Roster state
  const [roster, setRoster] = useState<StudentRosterItem[]>([]);
  const [submissionStatus, setSubmissionStatus] = useState<string>('NOT_STARTED');
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Modals
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showBulkNotAppModal, setShowBulkNotAppModal] = useState<boolean>(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  // Subject catalogues
  const subjectsMap: Record<string, { id: number; name: string; code: string }[]> = {
    class10: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 3, name: 'Science', code: 'SCI-103' },
      { id: 7, name: 'Hindi', code: 'HIN-107' },
      { id: 8, name: 'Social Science', code: 'SOC-108' },
      { id: 9, name: 'Sanskrit', code: 'SKT-109' },
      { id: 10, name: 'Punjabi', code: 'PJB-110' }
    ],
    class12_Science: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 4, name: 'Physics', code: 'PHY-104' },
      { id: 5, name: 'Chemistry', code: 'CHM-105' },
      { id: 6, name: 'Biology', code: 'BIO-106' },
      { id: 11, name: 'Computer Science', code: 'CSC-111' },
      { id: 12, name: 'Physical Education', code: 'PED-112' }
    ],
    class12_Arts: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 7, name: 'Hindi', code: 'HIN-107' },
      { id: 16, name: 'History', code: 'HIS-116' },
      { id: 17, name: 'Political Science', code: 'POL-117' },
      { id: 18, name: 'Geography', code: 'GEO-118' },
      { id: 15, name: 'Economics', code: 'ECO-115' },
      { id: 19, name: 'Sociology', code: 'SOC-119' },
      { id: 20, name: 'Psychology', code: 'PSY-120' },
      { id: 23, name: 'Home Science', code: 'HSC-123' }
    ],
    class12_Commerce: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 13, name: 'Accountancy', code: 'ACC-113' },
      { id: 14, name: 'Business Studies', code: 'BST-114' },
      { id: 15, name: 'Economics', code: 'ECO-115' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 22, name: 'Entrepreneurship', code: 'ENT-122' },
      { id: 21, name: 'Informatics Practices', code: 'IP-121' }
    ]
  };

  const currentSubjectList = useMemo(() => {
    if (selectedClass === 1) return subjectsMap.class10;
    if (selectedStream === 'Science') return subjectsMap.class12_Science;
    if (selectedStream === 'Commerce') return subjectsMap.class12_Commerce;
    return subjectsMap.class12_Arts;
  }, [selectedClass, selectedStream]);

  // Load Months
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const res = await fetchWithAuth('/api/monthly-assessments/months');
        const json = await res.json();
        if (json.success && json.data.length > 0) {
          setMonths(json.data);
        }
      } catch (e) {}
    };
    fetchMonths();
  }, []);

  // Sync selected subject when class or stream changes
  useEffect(() => {
    if (currentSubjectList.length > 0) {
      const exists = currentSubjectList.some(s => s.id === selectedSubject);
      if (!exists) {
        setSelectedSubject(currentSubjectList[0].id);
      }
    }
  }, [selectedClass, selectedStream, currentSubjectList]);

  // Load roster
  const loadRoster = async () => {
    setLoading(true);
    setMessage(null);
    setIsDirty(false);
    try {
      const query = new URLSearchParams({
        class_id: String(selectedClass),
        stream: selectedClass === 2 ? selectedStream : 'N/A',
        subject_id: String(selectedSubject),
        month: selectedMonth,
        assessment_type: 'MONTHLY'
      });
      const res = await fetchWithAuth(`/api/monthly-assessments/roster?${query.toString()}`);
      const json = await res.json();
      if (json.success) {
        const studentList = json.data?.students || json.data?.roster || [];
        setRoster(studentList);
        setMaximumMarks(json.data?.maximum_marks || (selectedMonth === 'September' ? 40 : 20));
        setSubmissionStatus(json.data?.submission_status || (studentList.some((s: any) => s.obtained_marks !== '' || s.status === 'ABSENT') ? 'IN_PROGRESS' : 'NOT_STARTED'));
      }
    } catch (e) {
      console.error('Error loading roster:', e);
      setMessage({ type: 'error', text: 'Failed to load student roster for selected criteria.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoster();
  }, [selectedMonth, selectedClass, selectedStream, selectedSubject]);

  // Handle Mark Change
  const handleMarkChange = (studentId: number, value: string) => {
    setIsDirty(true);
    setRoster(prev => prev.map(item => {
      if (item.student_id === studentId) {
        return {
          ...item,
          obtained_marks: value,
          status: 'PRESENT',
          is_absent: false
        };
      }
      return item;
    }));
  };

  // Handle Status Change (PRESENT, ABSENT, NOT_APPLICABLE)
  const handleStatusChange = (studentId: number, newStatus: 'PRESENT' | 'ABSENT' | 'NOT_APPLICABLE') => {
    setIsDirty(true);
    setRoster(prev => prev.map(item => {
      if (item.student_id === studentId) {
        return {
          ...item,
          status: newStatus,
          is_absent: newStatus === 'ABSENT',
          obtained_marks: newStatus === 'PRESENT' ? item.obtained_marks : '',
          remarks: newStatus === 'NOT_APPLICABLE' ? 'NOT_APPLICABLE' : (newStatus === 'ABSENT' ? 'ABSENT' : '')
        };
      }
      return item;
    }));
  };

  // Bulk Actions
  const handleMarkAllPresent = () => {
    setIsDirty(true);
    setRoster(prev => prev.map(item => ({
      ...item,
      status: 'PRESENT',
      is_absent: false,
      remarks: ''
    })));
  };

  const handleMarkAllAbsent = () => {
    setIsDirty(true);
    setRoster(prev => prev.map(item => ({
      ...item,
      status: 'ABSENT',
      is_absent: true,
      obtained_marks: '',
      remarks: 'ABSENT'
    })));
  };

  const handleMarkAllNotApplicable = () => {
    setShowBulkNotAppModal(false);
    setIsDirty(true);
    setRoster(prev => prev.map(item => ({
      ...item,
      status: 'NOT_APPLICABLE',
      is_absent: false,
      obtained_marks: '',
      remarks: 'NOT_APPLICABLE'
    })));
  };

  // Validation Statistics Calculation
  const stats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let notApp = 0;
    let marksEntered = 0;
    let marksMissing = 0;
    let invalidMarks = 0;

    for (const item of roster) {
      if (item.status === 'ABSENT') {
        absent++;
      } else if (item.status === 'NOT_APPLICABLE') {
        notApp++;
      } else {
        // PRESENT
        present++;
        if (item.obtained_marks === '' || item.obtained_marks === null || item.obtained_marks === undefined) {
          marksMissing++;
        } else {
          const val = parseFloat(String(item.obtained_marks));
          if (isNaN(val) || val < 0 || val > maximumMarks) {
            invalidMarks++;
          } else {
            marksEntered++;
          }
        }
      }
    }

    return {
      total: roster.length,
      present,
      absent,
      not_applicable: notApp,
      marks_entered: marksEntered,
      marks_missing: marksMissing,
      invalid_marks: invalidMarks,
      canSubmit: roster.length > 0 && marksMissing === 0 && invalidMarks === 0
    };
  }, [roster, maximumMarks]);

  // Save Draft
  const handleSaveDraft = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        class_id: selectedClass,
        stream: selectedClass === 2 ? selectedStream : null,
        subject_id: selectedSubject,
        month: selectedMonth,
        maximum_marks: maximumMarks,
        marks: roster
      };
      const res = await fetchWithAuth('/api/monthly-assessments/save-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.success) {
        setSubmissionStatus('DRAFT');
        setIsDirty(false);
        setMessage({ type: 'success', text: 'Draft marks saved successfully. You may continue editing.' });
      } else {
        setMessage({ type: 'error', text: json.message || 'Failed to save draft.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error saving draft.' });
    } finally {
      setSaving(false);
    }
  };

  // Final Submit
  const handleFinalSubmit = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        class_id: selectedClass,
        stream: selectedClass === 2 ? selectedStream : null,
        subject_id: selectedSubject,
        month: selectedMonth,
        maximum_marks: maximumMarks,
        marks: roster
      };
      const res = await fetchWithAuth('/api/monthly-assessments/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.success) {
        setSubmissionStatus('SUBMITTED');
        setIsDirty(false);
        setMessage({ type: 'success', text: 'Monthly Assessment marks submitted successfully to BEO/DEO.' });
      } else {
        setMessage({ type: 'error', text: json.message || 'Submission failed.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error submitting marks.' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = () => {
    switch (submissionStatus) {
      case 'APPROVED':
      case 'VERIFIED':
        return <span className="bg-emerald-100 text-emerald-800 font-bold px-3 py-1 rounded text-xs border border-emerald-300">Verified & Approved by BEO</span>;
      case 'SUBMITTED':
        return <span className="bg-blue-100 text-blue-800 font-bold px-3 py-1 rounded text-xs border border-blue-300">Submitted to BEO</span>;
      case 'DRAFT':
        return <span className="bg-amber-100 text-amber-800 font-bold px-3 py-1 rounded text-xs border border-amber-300">Draft In Progress</span>;
      case 'REVISION_REQUIRED':
        return <span className="bg-rose-100 text-rose-800 font-bold px-3 py-1 rounded text-xs border border-rose-300">Revision Requested by BEO</span>;
      default:
        return <span className="bg-slate-100 text-slate-700 font-bold px-3 py-1 rounded text-xs border border-slate-300">Not Started</span>;
    }
  };

  const isReadOnly = submissionStatus === 'APPROVED' || submissionStatus === 'VERIFIED';

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs font-bold text-blue-700 uppercase tracking-wider">
            Monthly Assessment Marks Entry Module
          </div>
          <h1 className="text-lg md:text-xl font-bold text-[#0F2C59]">
            Monthly Student Assessment & Scorecard Entry
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            Step 1: Month → Step 2: Class → Step 3: Stream → Step 4: Subject → Auto-Load Students → Save Draft / Submit
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {getStatusBadge()}
        </div>
      </div>

      {/* Alert Messages */}
      {message && (
        <div className={`p-3 rounded-lg border text-xs flex items-start justify-between ${
          message.type === 'success'
            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
            : 'bg-rose-50 border-rose-300 text-rose-900'
        }`}>
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-500 hover:text-slate-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 4-STEP SELECTOR BAR */}
      <div className="bg-[#0F2C59] p-4 rounded-lg text-white shadow-md grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Step 1: Month & Session */}
        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>Step 1: Month ({academicSession})</span>
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded focus:ring-1 focus:ring-amber-400"
          >
            {months.length > 0 ? (
              months.map(m => (
                <option key={m.id} value={m.month_name}>{m.month_name}</option>
              ))
            ) : (
              <>
                <option value="May">May</option>
                <option value="June">June</option>
                <option value="July">July</option>
                <option value="August">August</option>
                <option value="September">September</option>
                <option value="October">October</option>
                <option value="November">November</option>
                <option value="December">December</option>
                <option value="January">January</option>
                <option value="February">February</option>
                <option value="March">March</option>
              </>
            )}
          </select>
        </div>

        {/* Step 2: Class */}
        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <Layers className="w-3.5 h-3.5" />
            <span>Step 2: Class</span>
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(parseInt(e.target.value, 10))}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded focus:ring-1 focus:ring-amber-400"
          >
            <option value={1}>Class 10 (Secondary)</option>
            <option value={2}>Class 12 (Senior Secondary)</option>
          </select>
        </div>

        {/* Step 3: Stream */}
        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <BookOpen className="w-3.5 h-3.5" />
            <span>Step 3: Stream</span>
          </label>
          <select
            disabled={selectedClass === 1}
            value={selectedClass === 1 ? 'N/A' : selectedStream}
            onChange={(e) => setSelectedStream(e.target.value)}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded focus:ring-1 focus:ring-amber-400 disabled:opacity-40"
          >
            {selectedClass === 1 ? (
              <option value="N/A">Not Applicable (Class 10)</option>
            ) : (
              <>
                <option value="Arts">Arts (Humanities)</option>
                <option value="Science">Science</option>
                <option value="Commerce">Commerce</option>
              </>
            )}
          </select>
        </div>

        {/* Step 4: Subject */}
        <div>
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
            <ClipboardCheck className="w-3.5 h-3.5" />
            <span>Step 4: Subject</span>
          </label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(parseInt(e.target.value, 10))}
            className="w-full bg-[#081B38] border border-slate-700 text-white font-bold text-xs p-2 rounded focus:ring-1 focus:ring-amber-400"
          >
            {currentSubjectList.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
            ))}
          </select>
        </div>
      </div>

      {/* MARKS ENTRY ROSTER TABLE */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {/* Roster Header & Controls */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-bold text-[#0F2C59]">
                Student Assessment Roster • {selectedMonth} Assessment
              </h2>
              <span className="bg-amber-100 text-amber-900 text-xs font-extrabold px-2.5 py-0.5 rounded border border-amber-300">
                Maximum Marks: {maximumMarks}
              </span>
              {isDirty && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-300 animate-pulse">
                  Unsaved changes
                </span>
              )}
              {!isDirty && submissionStatus === 'DRAFT' && (
                <span className="bg-slate-100 text-slate-700 text-[10px] font-bold px-2 py-0.5 rounded border border-slate-300">
                  Saved Draft
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              <strong>{roster.length} students found</strong> for selected criteria • Present: <strong className="text-emerald-700">{stats.present}</strong> • Absent: <strong className="text-rose-700">{stats.absent}</strong> • N/A: <strong className="text-slate-600">{stats.not_applicable}</strong> • Missing Marks: <strong className={stats.marks_missing > 0 ? 'text-rose-600 font-bold' : 'text-slate-600'}>{stats.marks_missing}</strong>
            </div>
          </div>

          {/* Bulk Action Buttons & Save */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Bulk Actions */}
            {!isReadOnly && roster.length > 0 && (
              <div className="flex items-center space-x-1.5 mr-2">
                <button
                  type="button"
                  onClick={handleMarkAllPresent}
                  className="inline-flex items-center space-x-1 bg-white hover:bg-slate-100 text-emerald-800 border border-emerald-300 px-2.5 py-1.5 rounded font-bold text-[11px] shadow-2xs transition"
                >
                  <CheckSquare className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Mark All Present</span>
                </button>

                <button
                  type="button"
                  onClick={handleMarkAllAbsent}
                  className="inline-flex items-center space-x-1 bg-white hover:bg-slate-100 text-rose-800 border border-rose-300 px-2.5 py-1.5 rounded font-bold text-[11px] shadow-2xs transition"
                >
                  <XSquare className="w-3.5 h-3.5 text-rose-600" />
                  <span>Mark All Absent</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowBulkNotAppModal(true)}
                  className="inline-flex items-center space-x-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 px-2.5 py-1.5 rounded font-bold text-[11px] shadow-2xs transition"
                >
                  <Slash className="w-3.5 h-3.5 text-slate-500" />
                  <span>Mark All N/A</span>
                </button>
              </div>
            )}

            <button
              onClick={handleSaveDraft}
              disabled={saving || isReadOnly || roster.length === 0}
              className="inline-flex items-center space-x-1.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 px-3 py-1.5 rounded font-bold text-xs transition disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{saving ? 'Saving...' : 'Save Draft'}</span>
            </button>

            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={saving || isReadOnly || roster.length === 0}
              className="inline-flex items-center space-x-1.5 bg-[#0F2C59] hover:bg-[#1E40AF] text-white px-3.5 py-1.5 rounded font-bold text-xs shadow-sm transition disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
              <span>Submit Assessment</span>
            </button>
          </div>
        </div>

        {/* Student Table */}
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-xs">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-[#0F2C59] border-t-transparent mb-2"></div>
            <div className="font-bold text-slate-700 text-sm">Loading students...</div>
            <div className="text-slate-400 mt-1">Retrieving eligible student roster from database</div>
          </div>
        ) : roster.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-xs">
            <div className="font-bold text-slate-700 text-sm">No eligible students found for the selected Class/Stream/Subject.</div>
            <div className="text-slate-400 mt-1">Please verify class, stream, and school enrollment status.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-[#0F2C59] text-white uppercase font-bold text-[11px] border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3 text-center w-12">#</th>
                  <th className="py-2.5 px-3 w-28">SRN</th>
                  <th className="py-2.5 px-3.5">Student Name</th>
                  <th className="py-2.5 px-2.5 text-center w-16">Gender</th>
                  <th className="py-2.5 px-3 text-center w-24">Status</th>
                  <th className="py-2.5 px-3 text-center w-48">Attendance / Assessment Status</th>
                  <th className="py-2.5 px-3 text-center w-36">Marks (0–{maximumMarks})</th>
                  <th className="py-2.5 px-3 text-right w-24">Percentage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium">
                {roster.map((student, idx) => {
                  const marksNum = parseFloat(String(student.obtained_marks));
                  const isPresent = student.status === 'PRESENT';
                  const isAbsent = student.status === 'ABSENT';
                  const isNotApp = student.status === 'NOT_APPLICABLE';

                  const percentage = isPresent && !isNaN(marksNum)
                    ? ((marksNum / maximumMarks) * 100).toFixed(1)
                    : '-';

                  const isMarksInvalid = isPresent && (student.obtained_marks === '' || isNaN(marksNum) || marksNum < 0 || marksNum > maximumMarks);

                  return (
                    <tr
                      key={student.student_id}
                      className={`hover:bg-slate-50 transition ${
                        isAbsent ? 'bg-rose-50/40' : isNotApp ? 'bg-slate-50 text-slate-400' : ''
                      }`}
                    >
                      <td className="py-2.5 px-3 text-center font-bold text-slate-700">
                        {idx + 1}
                      </td>
                      <td className="py-2.5 px-3 text-slate-700 font-mono text-[11px] font-bold">
                        {student.srn}
                      </td>
                      <td className="py-2.5 px-3.5 font-bold text-slate-900">
                        <button
                          type="button"
                          onClick={() => setSelectedStudentId(student.student_id)}
                          className="text-left font-bold text-blue-900 hover:text-blue-700 hover:underline flex items-center gap-1.5"
                          title="Click to view complete student academic history"
                        >
                          <span>{student.student_name}</span>
                          <Eye className="w-3 h-3 text-blue-600 opacity-60 hover:opacity-100" />
                        </button>
                      </td>
                      <td className="py-2.5 px-2.5 text-center text-slate-600">
                        {student.gender}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-300">
                          {student.enrollment_status || 'ACTIVE'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <select
                          disabled={isReadOnly}
                          value={student.status}
                          onChange={(e) => handleStatusChange(student.student_id, e.target.value as any)}
                          className={`p-1 text-xs font-bold rounded border ${
                            isPresent
                              ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                              : isAbsent
                              ? 'bg-rose-50 text-rose-900 border-rose-300'
                              : 'bg-slate-100 text-slate-700 border-slate-300'
                          }`}
                        >
                          <option value="PRESENT">PRESENT</option>
                          <option value="ABSENT">ABSENT</option>
                          <option value="NOT_APPLICABLE">NOT APPLICABLE</option>
                        </select>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <input
                          type="number"
                          min="0"
                          max={maximumMarks}
                          step="0.5"
                          disabled={!isPresent || isReadOnly}
                          value={isPresent ? student.obtained_marks : ''}
                          onChange={(e) => handleMarkChange(student.student_id, e.target.value)}
                          placeholder={isPresent ? `0–${maximumMarks}` : '-'}
                          className={`w-28 text-center py-1 px-2 border rounded font-bold text-xs ${
                            !isPresent
                              ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                              : isMarksInvalid
                              ? 'bg-rose-50 border-rose-400 text-rose-900 focus:ring-1 focus:ring-rose-500'
                              : 'bg-white border-slate-300 focus:ring-1 focus:ring-[#0F2C59] text-slate-900'
                          }`}
                        />
                      </td>
                      <td className="py-2.5 px-3 text-right font-extrabold text-slate-900">
                        {isAbsent ? (
                          <span className="text-rose-600 font-bold text-[11px]">ABSENT</span>
                        ) : isNotApp ? (
                          <span className="text-slate-400 text-[11px]">N/A</span>
                        ) : percentage !== '-' ? (
                          <span className={Number(percentage) >= 60 ? 'text-emerald-700' : 'text-amber-700'}>
                            {percentage}%
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Actions & Summary */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="text-xs text-slate-600">
            {isReadOnly ? (
              <span className="text-emerald-700 font-bold">This assessment has been verified & approved. No further edits permitted.</span>
            ) : (
              <span>Review all marks and attendance carefully before final submission to BEO.</span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSaveDraft}
              disabled={saving || isReadOnly || roster.length === 0}
              className="inline-flex items-center space-x-1.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 px-4 py-2 rounded font-bold text-xs transition disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{saving ? 'Saving...' : 'Save Draft'}</span>
            </button>

            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={saving || isReadOnly || roster.length === 0}
              className="inline-flex items-center space-x-1.5 bg-[#0F2C59] hover:bg-[#1E40AF] text-white px-5 py-2 rounded font-bold text-xs shadow transition disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4 text-amber-400" />
              <span>Final Submit Assessment</span>
            </button>
          </div>
        </div>
      </div>

      {/* 1. SUBMISSION SUMMARY & CONFIRMATION MODAL */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden border border-slate-300 animate-in fade-in zoom-in duration-150">
            <div className="p-4 bg-[#0F2C59] text-white flex justify-between items-center">
              <div>
                <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                  Assessment Verification Summary
                </div>
                <h3 className="text-base font-bold">Confirm Assessment Submission</h3>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-1 rounded hover:bg-white/20 text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-1">
                <div className="text-slate-500 font-bold uppercase text-[10px]">Assessment Details</div>
                <div className="text-slate-900 font-semibold">
                  {selectedMonth} ({academicSession}) • {selectedClass === 1 ? 'Class 10' : `Class 12 (${selectedStream})`} • {currentSubjectList.find(s => s.id === selectedSubject)?.name} (Max: {maximumMarks})
                </div>
              </div>

              {/* STATS MATRIX TABLE */}
              <div>
                <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[11px] mb-2">
                  Student Verification Summary
                </h4>
                <div className="border border-slate-200 rounded overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <tbody className="divide-y divide-slate-200">
                      <tr className="bg-slate-50">
                        <td className="py-2 px-3 font-semibold text-slate-700">Total Enrolled Students Loaded</td>
                        <td className="py-2 px-3 text-right font-extrabold text-slate-900">{stats.total}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-medium text-emerald-800">Present Students</td>
                        <td className="py-2 px-3 text-right font-bold text-emerald-700">{stats.present}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-medium text-rose-800">Absent Students</td>
                        <td className="py-2 px-3 text-right font-bold text-rose-700">{stats.absent}</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-medium text-slate-600">Not Applicable Students</td>
                        <td className="py-2 px-3 text-right font-bold text-slate-700">{stats.not_applicable}</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="py-2 px-3 font-semibold text-slate-800">Marks Validly Entered</td>
                        <td className="py-2 px-3 text-right font-extrabold text-blue-700">{stats.marks_entered} / {stats.present}</td>
                      </tr>
                      {stats.marks_missing > 0 && (
                        <tr className="bg-rose-50">
                          <td className="py-2 px-3 font-bold text-rose-800">Marks Missing (Present Students)</td>
                          <td className="py-2 px-3 text-right font-extrabold text-rose-700">{stats.marks_missing}</td>
                        </tr>
                      )}
                      {stats.invalid_marks > 0 && (
                        <tr className="bg-rose-50">
                          <td className="py-2 px-3 font-bold text-rose-800">Invalid Marks (Out of Range)</td>
                          <td className="py-2 px-3 text-right font-extrabold text-rose-700">{stats.invalid_marks}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Warning / Validation alerts */}
              {!stats.canSubmit ? (
                <div className="p-3 bg-rose-50 border border-rose-300 rounded text-rose-900 text-xs flex items-start space-x-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block">Submission Blocked: Missing Marks</strong>
                    <span>Please enter marks (0–{maximumMarks}) for all {stats.marks_missing} Present students or mark them as Absent/Not Applicable before submitting.</span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-emerald-50 border border-emerald-300 rounded text-emerald-900 text-xs flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block">All Validation Checks Passed</strong>
                    <span>Marks are ready to be officially submitted and locked for BEO review.</span>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-200">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-semibold text-xs"
                >
                  Cancel & Review
                </button>
                <button
                  onClick={handleFinalSubmit}
                  disabled={!stats.canSubmit || saving}
                  className="px-4 py-2 bg-[#0F2C59] hover:bg-[#1E40AF] text-white rounded font-bold text-xs flex items-center space-x-1.5 disabled:opacity-40"
                >
                  <Check className="w-4 h-4 text-amber-400" />
                  <span>{saving ? 'Submitting...' : 'Confirm & Final Submit to BEO'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. BULK NOT APPLICABLE CONFIRMATION MODAL */}
      {showBulkNotAppModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden border border-slate-300 p-5 space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Mark All as Not Applicable?</h3>
                <p className="text-xs text-slate-600 mt-1">
                  This will set all {roster.length} students in this roster to <strong>NOT APPLICABLE</strong> and clear their marks.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t border-slate-200">
              <button
                onClick={() => setShowBulkNotAppModal(false)}
                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded font-semibold text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAllNotApplicable}
                className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded font-bold text-xs"
              >
                Yes, Mark All N/A
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Marks Entry Status Module & Dynamic Subject Matrix */}
      <div className="pt-4">
        <MarksEntryStatusModule
          defaultMonth={selectedMonth}
          title="School Assessment Marks Entry Status & Progress Matrix"
          onOpenStudent={(stId) => setSelectedStudentId(stId)}
        />
      </div>

      {/* Student Academic History Dossier Modal */}
      {selectedStudentId && (
        <StudentAcademicHistoryModal
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
        />
      )}
    </div>
  );
};
